import {
  AUCTION_REQUEST_ACCEPTED,
  AUCTION_REQUEST_CREATED,
  AUCTION_REQUEST_DECLINED,
  AUCTION_REQUEST_REJECTED,
  AUCTION_REQUEST_SENT,
  AUCTION_REQUEST_UNAVAILABLE,
  CAREPROVIDER_CREATED,
  CAREPROVIDER_INACTIVE,
  DISTANCE_FILTER_RECEIVER,
  FILTER_TYPES,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_SUCCEED,
  RECEIVER_INACTIVE_REASONS,
} from "@recare/core/consts";
import { useDateLocale } from "@recare/core/hooks/useDateLocale";
import { declineDetailsReason } from "@recare/core/model/auctions";
import { formatUnixDate } from "@recare/core/model/utils/dates";
import { useGetOntology } from "@recare/core/model/utils/ontologies/hooks";
import { grammaticalList } from "@recare/core/model/utils/strings";
import {
  Auction,
  AuctionRequest,
  Careprovider,
  EventFilter,
  GetOntologyType,
  Patient,
  QueryProgress,
  WarningsPayload,
} from "@recare/core/types";
import {
  Locale as LocaleString,
  useLocale,
  useTranslations,
} from "@recare/translations";
import Translations from "@recare/translations/types";
import { GetCareproviderWarnings } from "apollo/queries";
import { VerticalLayout } from "ds/materials/layouts";
import { margin } from "ds/materials/metrics";
import { Body } from "ds/materials/typography";
import { activateFax } from "dsl/atoms/FaxPatient";
import WarningMessageAtom from "dsl/atoms/WarningMessage";
import { getNoCapacityReasonsMessage } from "dsl/ecosystems/CareproviderSettingsCapacity";
import { useEffect, useMemo } from "react";

const HARD_ERRORS = [
  FILTER_TYPES.FILTER_SOLUTION,
  FILTER_TYPES.FILTER_REMOVED_SOLUTION,
  FILTER_TYPES.FILTER_BLACKLISTED_INSURANCE,
  FILTER_TYPES.FILTER_SENDER_BLACKLISTED,
  FILTER_TYPES.FILTER_RECEIVER_BLACKLISTED,
];

function getExistingRequestWarningMessage(
  auctionrequest: AuctionRequest | null | undefined,
  translations: Translations,
  locale: Locale,
  getOntology: GetOntologyType,
): string | null {
  if (!auctionrequest) return null;

  switch (auctionrequest.status) {
    case AUCTION_REQUEST_REJECTED:
      return translations.patient.addCareprovider.errorRequestRejected;
    case AUCTION_REQUEST_ACCEPTED: {
      return `${translations.patient.addCareprovider.errorRequestAccepted}\n${translations.patient.addCareprovider.pleaseGoToAnswer}`;
    }

    case AUCTION_REQUEST_SENT:
    case AUCTION_REQUEST_CREATED: {
      return translations.patient.addCareprovider
        .errorRequestSentAutomaticSearch;
    }
    case AUCTION_REQUEST_DECLINED: {
      const message = declineDetailsReason({
        getOntology,
        options: auctionrequest.response,
        searchType: auctionrequest.careprovider?.patient_type,
        translations,
        withHyphen: true,
        locale,
      });
      return translations.patient.addCareprovider.errorRequestDeclined({
        message: message || "",
      });
    }
    case AUCTION_REQUEST_UNAVAILABLE:
      return "";
    default:
      return translations.patient.addCareprovider.errorContactRecare;
  }
}

export const getWarningMessage = ({
  careprovider,
  faxActivated,
  forProviderRecommendations,
  getOntology,
  locale,
  localeString,
  reason,
  request,
  translations,
}: {
  careprovider: Careprovider;
  faxActivated?: boolean | number | null;
  forProviderRecommendations?: boolean;
  getOntology: GetOntologyType;
  locale: Locale;
  localeString: LocaleString;
  reason: EventFilter;
  request?: AuctionRequest;
  translations: Translations;
}): string | null => {
  const inactiveReason = careprovider?.inactive_reason;
  const showInactiveReason =
    inactiveReason === RECEIVER_INACTIVE_REASONS.UNNNEEDED_CHAIN_CENTER ||
    inactiveReason === RECEIVER_INACTIVE_REASONS.UNNNEEDED_CHAIN_SUBSIDIARY;

  switch (reason.type) {
    case FILTER_TYPES.FILTER_BLACKLISTED_INSURANCE:
      return translations.patient.addCareprovider.insuranceBlacklisted;
    case FILTER_TYPES.FILTER_RECEIVER_BLACKLISTED:
      return translations.careprovider.providerNotAvailable;
    case FILTER_TYPES.FILTER_SENDER_BLACKLISTED:
      return translations.patient.addCareprovider.warningWhitelist;
    case FILTER_TYPES.FILTER_THROTTLE:
      return translations.patient.addCareprovider.providerThrottled;
    case FILTER_TYPES.FILTER_STATUS:
      if (careprovider.status === CAREPROVIDER_CREATED && !faxActivated)
        return translations.patient.addCareprovider.errorProviderCreated;
      if (careprovider.status === CAREPROVIDER_INACTIVE && showInactiveReason) {
        return getOntology({
          type: "receiverInactiveReason",
          key: careprovider?.inactive_reason,
        });
      }
      return faxActivated && !forProviderRecommendations
        ? translations.patient.addCareprovider.contactingByFax
        : translations.patient.addCareprovider.errorProviderNotActive;

    case FILTER_TYPES.FILTER_NO_BED_AVAILABLE:
      return translations.patient.addCareprovider.noBedsAvailable;
    case FILTER_TYPES.FILTER_CAPACITY_START_DATE:
      return careprovider?.profile?.capacity?.available_date
        ? translations.patient.addCareprovider.warningNoCapacity({
            startDate: formatUnixDate(
              careprovider.profile.capacity.available_date,
              localeString,
            ),
          })
        : "";

    case FILTER_TYPES.FILTER_ZIPCODES:
      return translations.patient.addCareprovider.warningNotCatchment;
    case FILTER_TYPES.FILTER_SERVICE:
      return translations.patient.addCareprovider.warningNoService({
        service: (reason.values || [reason.value || -1])
          .map((key) => getOntology({ type: "service", key }))
          .join(", "),
      });
    case FILTER_TYPES.FILTER_DISTANCE:
      if (reason?.value === DISTANCE_FILTER_RECEIVER) {
        return translations.patient.addCareprovider
          .warningDistanceCareproviderRadius;
      }
      return translations.patient.addCareprovider.warningDistance;
    case FILTER_TYPES.FILTER_PREREQUISITE:
      return translations.patient.addCareprovider.warningPrerequisite({
        service: (reason.values || [reason.value || -1])
          .map((key) => getOntology({ type: "service", key }))
          .join(", "),
      });
    case FILTER_TYPES.FILTER_SOLUTION:
      return translations.patient.addCareprovider.errorNoSolution({
        solution: getOntology({ type: "solution", key: reason.value }),
      });
    case FILTER_TYPES.FILTER_CONTACTED:
      return getExistingRequestWarningMessage(
        request,
        translations,
        locale,
        getOntology,
      );
    case FILTER_TYPES.FILTER_CAPACITY_BACKOFF:
      if (!reason.no_capacity_expiration) return null;

      return `${translations.patient.addCareprovider.warningNoCapacity({
        startDate: formatUnixDate(reason.no_capacity_expiration, localeString),
      })}: ${getNoCapacityReasonsMessage({
        translations,
        getOntology,
        reasons: reason.no_capacity_reasons,
      })}`;
    case FILTER_TYPES.FILTER_SPECIALIZATION:
      return translations.patient.addCareprovider.errorNoSolution({
        solution:
          grammaticalList({
            array: reason.values,
            ontologyType: "specializations",
            getOntology,
            translations,
          }) || "",
      });
    case FILTER_TYPES.FILTER_AGE:
      if (!careprovider.profile?.accepted_age) return null;

      return translations.patient.addCareprovider.warningAge({
        minAge: (careprovider.profile.accepted_age.min || 0).toString(),
        maxAge: (careprovider.profile?.accepted_age?.max || 120).toString(),
      });
    case FILTER_TYPES.FILTER_WEIGHT:
      if (!careprovider.profile?.accepted_weight?.max) return null;

      return translations.patient.addCareprovider.title.warningWeight({
        maxWeight: careprovider.profile?.accepted_weight?.max.toString(),
      });

    default: {
      console.error(
        `Could not handle filter type ${reason.type} in provider warnings`,
        {
          careprovider_id: careprovider?.id,
          reason_value: JSON.stringify({
            value: reason.value,
            values: reason.values,
          }),
        },
      );
      return null;
    }
  }
};

type RotatedReasons = {
  [filterReason: string]: { reason: EventFilter; solution: number }[];
};

export function rotateReasons(
  warningsPayload: WarningsPayload | null | undefined,
  compatibleSolutions: Array<number>,
) {
  const result: RotatedReasons = {};

  Object.entries(warningsPayload?.solution_filter_reasons ?? {})?.forEach(
    ([solutionString, filters]: [string, EventFilter[]]) => {
      filters.forEach((r) => {
        if (!result[r.type]) result[r.type] = [];
        result[r.type].push({ solution: parseInt(solutionString), reason: r });
      });
    },
  );

  const availableSolutions = compatibleSolutions.filter((solution: number) => {
    const filteredOutSolutions =
      result?.[FILTER_TYPES.FILTER_SOLUTION]?.map(
        ({ solution }: { solution: number }) => solution,
      ) || [];
    const isCompatibleSolutionFiltered =
      filteredOutSolutions.includes(solution);
    return !isCompatibleSolutionFiltered;
  });

  if (availableSolutions?.length > 0)
    delete result[FILTER_TYPES.FILTER_SOLUTION];

  return { result, availableSolutions };
}

export function isError(
  warningReasons: Array<string>,
  isStopSearch: boolean,
  existingRequest: AuctionRequest | null | undefined,
): boolean {
  const isErrorMessage = warningReasons.some((r) => {
    return HARD_ERRORS.includes(parseInt(r));
  });

  if (isStopSearch) {
    if (existingRequest?.status === AUCTION_REQUEST_ACCEPTED) return true;
    else if (existingRequest) return false;
    else return isErrorMessage;
  }
  return isErrorMessage || !!existingRequest;
}

const showWarningHints = ({
  faxActivated,
  isErrorMessage,
  isStopSearch,
  requestStatus,
}: {
  faxActivated: boolean;
  isErrorMessage: boolean;
  isStopSearch: boolean;
  requestStatus: number | undefined;
}): { stopSearchHint: boolean; suggestionsHint: boolean } => {
  const showWarningHint = !isErrorMessage && !faxActivated;

  if (!showWarningHint)
    return { suggestionsHint: false, stopSearchHint: false };

  if (isStopSearch) {
    if (
      !requestStatus ||
      [
        AUCTION_REQUEST_DECLINED,
        AUCTION_REQUEST_SENT,
        AUCTION_REQUEST_REJECTED,
      ].includes(requestStatus)
    )
      return { suggestionsHint: false, stopSearchHint: true };
    return { suggestionsHint: false, stopSearchHint: false };
  }

  return { stopSearchHint: false, suggestionsHint: true };
};

export function WarningChecker({
  auction,
  isStopSearch,
  locale,
  localeString,
  patient,
  rotatedReasons,
  setExistingRequest,
  setIsErrorMessage,
  translations,
  warningReasons,
  warningsPayload,
}: {
  auction: Auction;
  isStopSearch: boolean;
  locale: Locale;
  localeString: LocaleString;
  patient: Patient;
  rotatedReasons: RotatedReasons;
  setExistingRequest: (request: AuctionRequest | null | undefined) => void;
  setIsErrorMessage: (isErrorMessage: boolean) => void;
  translations: Translations;
  warningReasons: string[];
  warningsPayload: WarningsPayload;
}) {
  const getOntology = useGetOntology();

  const request = warningsPayload?.auction_request;
  const faxActivated = activateFax({
    patient,
    auction,
    careprovider: warningsPayload.careprovider,
  });
  const isErrorMessage = isError(warningReasons, isStopSearch, request);

  setIsErrorMessage(isErrorMessage);
  setExistingRequest(request);

  const warningMessages = warningReasons
    .map((r) =>
      rotatedReasons[r].map(({ reason }) =>
        getWarningMessage({
          reason,
          careprovider: warningsPayload.careprovider,
          request,
          translations,
          getOntology,
          locale,
          localeString,
          faxActivated,
        }),
      ),
    )
    .flat()
    .truthy();

  const deduplicated = warningMessages.filter(
    (warningMessage: string, i: number) =>
      warningMessages.indexOf(warningMessage) === i,
  );

  const { stopSearchHint, suggestionsHint } = showWarningHints({
    faxActivated,
    isErrorMessage,
    isStopSearch,
    requestStatus: request?.status,
  });

  if (!deduplicated && !suggestionsHint && !stopSearchHint) return null;
  return (
    <>
      <VerticalLayout flexShrink={1} margin={margin(3, 0, 0, 2)}>
        {deduplicated.map((text: string, i: number) => (
          <WarningMessageAtom key={`${i}`}>{text}</WarningMessageAtom>
        ))}
        {suggestionsHint && (
          <Body margin={margin(1, 2, 0, 5)} fontStyle="italic">
            {translations.patient.addCareprovider.smartSuggestionsWarningHint}
          </Body>
        )}
        {stopSearchHint && (
          <Body margin={margin(1, 2, 0, 5)} fontStyle="italic">
            {translations.patient.addCareprovider.stopSearchWarningHint}
          </Body>
        )}
      </VerticalLayout>
    </>
  );
}

const BackendWarnings = ({
  auction,
  compatibleSolutions,
  isStopSearch,
  patient,
  queryProgress,
  setExistingRequest,
  setFilteredSolutions,
  setIsErrorMessage,
  warningsPayload,
}: {
  auction: Auction;
  compatibleSolutions: number[];
  isStopSearch: boolean;
  patient: Patient;
  queryProgress: QueryProgress;
  setExistingRequest: (request: AuctionRequest | null | undefined) => void;
  setFilteredSolutions: (solutions: number[]) => void;
  setIsErrorMessage: (isErrorMessage: boolean) => void;
  warningsPayload: WarningsPayload | undefined;
}) => {
  const translations = useTranslations();
  const localeString = useLocale();
  const { currentLocale: locale } = useDateLocale();

  const { availableSolutions, result: rotatedReasons } = useMemo(
    () => rotateReasons(warningsPayload, compatibleSolutions),
    [warningsPayload, compatibleSolutions],
  );

  useEffect(() => {
    if (setFilteredSolutions && !isStopSearch)
      setFilteredSolutions(availableSolutions);
  }, [availableSolutions?.length]);

  const warningReasons = Object.keys(rotatedReasons);

  if (queryProgress === QUERY_PROGRESS_FAILED)
    return <Body>{translations.auctionRequest.tryAgain}</Body>;

  if (!warningsPayload || !warningReasons) {
    if (queryProgress === QUERY_PROGRESS_SUCCEED) {
      setIsErrorMessage(false);
    }
    setExistingRequest(null);
    return <></>;
  }

  return (
    <WarningChecker
      locale={locale}
      localeString={localeString}
      patient={patient}
      auction={auction}
      rotatedReasons={rotatedReasons}
      isStopSearch={isStopSearch}
      setExistingRequest={setExistingRequest}
      setIsErrorMessage={setIsErrorMessage}
      translations={translations}
      warningsPayload={warningsPayload}
      warningReasons={warningReasons}
    />
  );
};

// to be deprecated with search merge
export default function CareproviderWarnings({
  auction,
  careproviderId,
  compatibleSolutions,
  isStopSearch,
  patient,
  setExistingRequest,
  setFilteredSolutions,
  setIsErrorMessage,
}: {
  auction: Auction;
  careproviderId: number;
  compatibleSolutions: Array<number>;
  isStopSearch: boolean;
  patient: Patient;
  setExistingRequest: (request: AuctionRequest | null | undefined) => void;
  setFilteredSolutions: (solutions: number[]) => void;
  setIsErrorMessage: (isErrorMessage: boolean) => void;
}) {
  return (
    <GetCareproviderWarnings
      auctionId={auction.id}
      careproviderId={careproviderId}
    >
      {(warningsPayload, queryProgress) => (
        <BackendWarnings
          auction={auction}
          compatibleSolutions={compatibleSolutions}
          isStopSearch={isStopSearch}
          patient={patient}
          setIsErrorMessage={setIsErrorMessage}
          setExistingRequest={setExistingRequest}
          setFilteredSolutions={setFilteredSolutions}
          warningsPayload={warningsPayload}
          queryProgress={queryProgress}
        />
      )}
    </GetCareproviderWarnings>
  );
}
